import CheckIcon from "@mui/icons-material/Check";
import { Button, CircularProgress, Dialog } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import {
  InvoiceDialogCloseButton,
  invoiceDesignFontFamily,
  invoiceDesignTokens,
  invoiceDialogPaperSx,
  invoiceModalActionSx,
} from "./InvoiceDesignSystem";
import {
  getValidInvoiceEmailRecipient,
  isInvoiceEmailDeliveryPendingError,
} from "./invoiceDeliveryFeedback";
import ActionSuccessDialog from "../../common/dashboard/ActionSuccessDialog";
import ManageInvoiceActionConfirmationDialog from "./ManageInvoices/ManageInvoiceActionConfirmationDialog";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  NETWORK_ERROR_TEXT,
  UserFacingError,
} from "../../../utils/userFacingError";
import {
  getInvoiceNoSavedEmailText,
  INVOICE_EMAIL_UNCONFIRMED_TEXT,
} from "./invoiceEmailText";
const INVOICE_PRINT_FALLBACK = buildStaffErrorFallback("print the invoice");

export type InvoiceCreatedEmailResult = Readonly<{
  actualRecipientEmail: string;
  successMessage: string;
  // Bold parts of the design success message (recipient and member).
  successHighlights?: readonly string[];
  // Routing or refresh details that must stay visible after success.
  successNote?: string;
}>;

type InvoiceCreatedDialogProps = Readonly<{
  actionError?: string | null;
  // Shown in the design email confirm: "{member} ({email})".
  customerName?: string | null;
  emailing?: boolean;
  message: string;
  onClose: () => void;
  onEmail?: () => Promise<InvoiceCreatedEmailResult>;
  onPrint: () => Promise<void> | void;
  open: boolean;
  printing?: boolean;
  recipientEmail?: string | null;
  title: string;
}>;

const InvoiceCreatedDialog = ({
  actionError,
  customerName,
  emailing = false,
  message,
  onClose,
  onEmail,
  onPrint,
  open,
  printing = false,
  recipientEmail,
  title,
}: InvoiceCreatedDialogProps): React.ReactElement => {
  const [emailConfirmationOpen, setEmailConfirmationOpen] = useState(false);
  const [emailActionInFlight, setEmailActionInFlight] = useState(false);
  const [emailActionError, setEmailActionError] = useState<string | null>(null);
  // UX audit INV-11: a Sending or Unknown result keeps Email Invoice off.
  const [emailDeliveryPending, setEmailDeliveryPending] = useState(false);
  const [emailSuccessMessage, setEmailSuccessMessage] = useState<string | null>(
    null,
  );
  const [emailSuccessHighlights, setEmailSuccessHighlights] = useState<
    readonly string[]
  >([]);
  const [emailSuccessNote, setEmailSuccessNote] = useState<string | null>(null);
  const [printActionInFlight, setPrintActionInFlight] = useState(false);
  const [printActionError, setPrintActionError] = useState<string | null>(null);
  const emailActionInFlightRef = useRef(false);
  const printActionInFlightRef = useRef(false);
  const validRecipientEmail = getValidInvoiceEmailRecipient(recipientEmail);
  const actionInFlight =
    emailing || emailActionInFlight || printing || printActionInFlight;

  useEffect(() => {
    if (open) {
      return;
    }

    emailActionInFlightRef.current = false;
    setEmailActionInFlight(false);
    setEmailActionError(null);
    setEmailDeliveryPending(false);
    setEmailConfirmationOpen(false);
    setEmailSuccessMessage(null);
    setEmailSuccessHighlights([]);
    setEmailSuccessNote(null);
    printActionInFlightRef.current = false;
    setPrintActionInFlight(false);
    setPrintActionError(null);
  }, [open]);

  useEffect(() => {
    emailActionInFlightRef.current = false;
    setEmailActionInFlight(false);
    setEmailActionError(null);
    setEmailConfirmationOpen(false);
    setEmailSuccessMessage(null);
    setEmailSuccessHighlights([]);
    setEmailSuccessNote(null);
    setPrintActionError(null);
  }, [recipientEmail]);

  const handleOpenEmailConfirmation = () => {
    if (
      !onEmail ||
      !validRecipientEmail ||
      actionInFlight ||
      emailDeliveryPending
    ) {
      return;
    }

    setEmailActionError(null);
    setEmailConfirmationOpen(true);
  };

  const handleConfirmEmail = async () => {
    if (
      !onEmail ||
      !validRecipientEmail ||
      emailActionInFlightRef.current ||
      emailSuccessMessage ||
      emailDeliveryPending
    ) {
      return;
    }

    emailActionInFlightRef.current = true;
    setEmailActionError(null);
    setEmailActionInFlight(true);

    try {
      const authoritativeResult = await onEmail();
      const actualRecipientEmail = getValidInvoiceEmailRecipient(
        authoritativeResult.actualRecipientEmail,
      );
      const normalizedSuccessMessage = authoritativeResult.successMessage.trim();

      if (!actualRecipientEmail || !normalizedSuccessMessage) {
        throw new UserFacingError(INVOICE_EMAIL_UNCONFIRMED_TEXT);
      }

      setEmailSuccessMessage(normalizedSuccessMessage);
      setEmailSuccessHighlights(authoritativeResult.successHighlights || []);
      setEmailSuccessNote(authoritativeResult.successNote?.trim() || null);
      setEmailConfirmationOpen(false);
    } catch (error) {
      setEmailDeliveryPending(isInvoiceEmailDeliveryPendingError(error));
      const text = getUserFacingErrorText(error, INVOICE_EMAIL_UNCONFIRMED_TEXT);
      // A lost connection does not prove the email was not sent.
      setEmailActionError(
        text === NETWORK_ERROR_TEXT ? INVOICE_EMAIL_UNCONFIRMED_TEXT : text,
      );
    } finally {
      emailActionInFlightRef.current = false;
      setEmailActionInFlight(false);
    }
  };

  const handlePrint = async () => {
    if (actionInFlight || printActionInFlightRef.current) {
      return;
    }

    printActionInFlightRef.current = true;
    setPrintActionInFlight(true);
    setPrintActionError(null);
    try {
      await onPrint();
    } catch (error) {
      setPrintActionError(getUserFacingErrorText(error, INVOICE_PRINT_FALLBACK));
    } finally {
      printActionInFlightRef.current = false;
      setPrintActionInFlight(false);
    }
  };

  return (
    <>
      <Dialog
        onClose={actionInFlight ? undefined : onClose}
        open={open && !emailConfirmationOpen && !emailSuccessMessage}
        PaperProps={{ sx: invoiceDialogPaperSx({ maxWidth: 440 }) }}
      >
        <div
          className="relative bg-white px-7 py-[30px] text-center"
          style={{ fontFamily: invoiceDesignFontFamily }}
        >
          <InvoiceDialogCloseButton
            ariaLabel="Close invoice created dialog"
            disabled={actionInFlight}
            onClick={onClose}
          />
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#12B981] text-white">
            <CheckIcon sx={{ fontSize: 42 }} />
          </span>
          <h2 className="mt-[18px] text-sm font-semibold text-[#0A0A0A]">
            {title}
          </h2>
          <p className="mx-auto mt-2 max-w-[340px] text-sm leading-[1.55] text-[#0A0A0A]">
            {message}
          </p>

          {!emailSuccessMessage && onEmail && !validRecipientEmail ? (
            <p className="mt-4 text-sm font-semibold text-red-600" role="alert">
              {getInvoiceNoSavedEmailText({
                document: "invoice",
                printLabel: "Print Invoice",
              })}
            </p>
          ) : null}
          {!emailSuccessMessage &&
          (printActionError || emailActionError || actionError) ? (
            <p className="mt-4 text-sm font-semibold text-red-600" role="alert">
              {printActionError || emailActionError || actionError}
            </p>
          ) : null}

          <div className="mt-[22px] flex flex-wrap justify-center gap-3">
            {emailSuccessMessage ? null : (
              <>
                <Button
                  disabled={actionInFlight}
                  onClick={() => void handlePrint()}
                  startIcon={
                    printing || printActionInFlight ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : undefined
                  }
                  sx={{
                    ...invoiceModalActionSx({
                      backgroundColor: invoiceDesignTokens.gold,
                      hoverBackgroundColor: invoiceDesignTokens.goldDark,
                    }),
                    flex: "1 1 150px",
                    maxWidth: 170,
                  }}
                  variant="contained"
                >
                  Print Invoice
                </Button>
                {onEmail ? (
                  <Button
                    disabled={
                      actionInFlight ||
                      !validRecipientEmail ||
                      emailDeliveryPending
                    }
                    onClick={handleOpenEmailConfirmation}
                    startIcon={
                      emailing ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : undefined
                    }
                    sx={{
                      ...invoiceModalActionSx({
                        backgroundColor: invoiceDesignTokens.gold,
                        hoverBackgroundColor: invoiceDesignTokens.goldDark,
                      }),
                      flex: "1 1 150px",
                      maxWidth: 170,
                    }}
                    variant="contained"
                  >
                    Email Invoice
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </Dialog>

      <ActionSuccessDialog
        highlights={emailSuccessHighlights}
        message={open ? emailSuccessMessage : null}
        note={emailSuccessNote}
        onClose={onClose}
      />

      <ManageInvoiceActionConfirmationDialog
        customerName={customerName || ""}
        emailDeliveryPending={emailDeliveryPending}
        errorMessage={actionError || emailActionError}
        kind="EMAIL_INVOICE"
        loading={emailing || emailActionInFlight || !!emailSuccessMessage}
        onClose={() => {
          if (!actionInFlight) {
            setEmailActionError(null);
            setEmailConfirmationOpen(false);
          }
        }}
        onConfirm={handleConfirmEmail}
        open={open && emailConfirmationOpen}
        recipientEmail={validRecipientEmail}
      />
    </>
  );
};

export default InvoiceCreatedDialog;
